import type { WatchlistItem } from '../services/watchlist'

export type WatchlistTypeFilter = 'all' | 'companies' | 'persons'
export type WatchlistSortKey = 'name' | 'recent'

export function parseWatchlistTypeFilter(raw: string | null): WatchlistTypeFilter {
  if (raw === 'companies' || raw === 'persons') return raw
  return 'all'
}

export function serialiseWatchlistTypeFilter(id: WatchlistTypeFilter): string | null {
  if (id === 'all') return null
  return id
}

export function parseWatchlistSortKey(raw: string | null): WatchlistSortKey {
  if (raw === 'recent') return 'recent'
  return 'name'
}

export function serialiseWatchlistSortKey(key: WatchlistSortKey): string | null {
  if (key === 'name') return null
  return key
}

export function itemMatchesWatchlistQuery(
  item: WatchlistItem,
  rawQuery: string
): boolean {
  const q = rawQuery.trim().toLowerCase()
  if (!q) return true
  const haystack = [item.displayName, item.entityId]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(q)
}

export function itemMatchesWatchlistType(
  item: WatchlistItem,
  type: WatchlistTypeFilter
): boolean {
  if (type === 'all') return true
  if (type === 'companies') return item.entityType === 'company'
  return item.entityType === 'person'
}

export function filterWatchlistItems(
  items: WatchlistItem[],
  opts: { type: WatchlistTypeFilter; query: string }
): WatchlistItem[] {
  return items.filter(
    (item) =>
      itemMatchesWatchlistType(item, opts.type) &&
      itemMatchesWatchlistQuery(item, opts.query)
  )
}

export function sortWatchlistItems(
  items: WatchlistItem[],
  sortKey: WatchlistSortKey
): WatchlistItem[] {
  const copy = [...items]
  if (sortKey === 'name') {
    copy.sort((a, b) =>
      (a.displayName || a.entityId).localeCompare(
        b.displayName || b.entityId,
        'da'
      )
    )
    return copy
  }
  copy.sort((a, b) => {
    const byDate = b.createdAt.localeCompare(a.createdAt)
    if (byDate !== 0) return byDate
    return (a.displayName || a.entityId).localeCompare(
      b.displayName || b.entityId,
      'da'
    )
  })
  return copy
}

export function applyWatchlistItemFilters(
  items: WatchlistItem[],
  opts: {
    type: WatchlistTypeFilter
    query: string
    sort: WatchlistSortKey
  }
): WatchlistItem[] {
  return sortWatchlistItems(
    filterWatchlistItems(items, { type: opts.type, query: opts.query }),
    opts.sort
  )
}

export function watchlistFiltersActive(
  type: WatchlistTypeFilter,
  query: string
): boolean {
  return type !== 'all' || query.trim().length > 0
}
